import React, { useMemo, useRef } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  ChartOptions,
  ChartData,
  Plugin,
} from "chart.js";

// Registro único dos elementos do Chart.js usados por este gráfico de linha
// (mesmo padrão do componente de dashboard existente, sem dependências novas).
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

export interface ProductionChartColors {
  realized: string;
  realizedFill: string;
  meta: string;
  projected: string;
  todayLine: string;
  lastPoint: string;
  text: string;
  grid: string;
}

export interface ProductionChartProps {
  /** Datas cruas (yyyy-MM-dd) alinhadas às séries, para o tooltip. */
  rawDates: string[];
  /** Rótulos do eixo X já formatados (dd/MM). */
  labels: string[];
  /** Volume realizado por dia; null nos dias só de projeção. */
  realized: Array<number | null>;
  /** Meta diária (linha de referência), repetida em cada ponto. */
  meta: number[];
  /** Volume projetado; null no trecho realizado (exceto o ponto de junção). */
  projected: Array<number | null>;
  /** Índice do último dia real (divisor "hoje"); -1 quando não há série. */
  todayIndex: number;
  colors: ProductionChartColors;
  locale: string;
  labelsText: {
    realized: string;
    meta: string;
    projected: string;
    today: string;
    unit: string;
  };
  /** Altura do gráfico em px. */
  height?: number;
}

const formatNumber = (value: number, locale: string, decimals = 0): string =>
  Number(value).toLocaleString(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

const ProductionChart: React.FC<ProductionChartProps> = ({
  rawDates,
  labels,
  realized,
  meta,
  projected,
  todayIndex,
  colors,
  locale,
  labelsText,
  height = 360,
}) => {
  // react-chartjs-2 v5 injeta `plugins` só na criação do gráfico (nunca em update()), então
  // plugins inline congelam o closure do 1º render. Lemos os valores dinâmicos via refs para o
  // divisor "hoje" refletir os props atuais em cada draw (ex.: ao trocar o período/turno).
  const todayIndexRef = useRef(todayIndex);
  const todayLineColorRef = useRef(colors.todayLine);
  const textColorRef = useRef(colors.text);
  const todayLabelRef = useRef(labelsText.today);
  todayIndexRef.current = todayIndex;
  todayLineColorRef.current = colors.todayLine;
  textColorRef.current = colors.text;
  todayLabelRef.current = labelsText.today;

  const data = useMemo<ChartData<"line">>(() => {
    const lastProjectedIndex = (() => {
      for (let i = projected.length - 1; i >= 0; i -= 1) {
        if (projected[i] != null) return i;
      }
      return -1;
    })();

    return {
      labels,
      datasets: [
        {
          label: labelsText.meta,
          data: meta,
          borderColor: colors.meta,
          borderWidth: 2,
          borderDash: [6, 4],
          pointRadius: 0,
          pointHitRadius: 0,
          fill: false,
          tension: 0,
          order: 3,
        },
        {
          label: labelsText.realized,
          data: realized,
          borderColor: colors.realized,
          backgroundColor: colors.realizedFill,
          borderWidth: 3,
          fill: "origin",
          tension: 0.25,
          spanGaps: false,
          pointRadius: realized.map((v, i) => (v == null ? 0 : i === todayIndex ? 5 : 3)),
          pointBackgroundColor: realized.map((_, i) =>
            i === todayIndex ? colors.lastPoint : colors.realized,
          ),
          pointBorderColor: "#ffffff",
          pointBorderWidth: 2,
          order: 1,
        },
        {
          label: labelsText.projected,
          data: projected,
          borderColor: colors.projected,
          borderWidth: 3,
          borderDash: [6, 5],
          fill: false,
          tension: 0.25,
          spanGaps: true,
          pointRadius: projected.map((v, i) => (v != null && i === lastProjectedIndex ? 5 : 0)),
          pointBackgroundColor: colors.projected,
          pointBorderColor: "#ffffff",
          pointBorderWidth: 2,
          order: 2,
        },
      ],
    };
  }, [labels, realized, meta, projected, todayIndex, colors, labelsText]);

  const options = useMemo<ChartOptions<"line">>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: colors.lastPoint,
          padding: 12,
          cornerRadius: 8,
          titleFont: { weight: 600 },
          filter: (item) => item.raw !== null && item.raw !== undefined,
          callbacks: {
            title: (items) => {
              const idx = items[0]?.dataIndex ?? 0;
              const raw = rawDates[idx];
              if (!raw) return "";
              const [y, m, d] = raw.split("-").map(Number);
              return new Date(y, m - 1, d).toLocaleDateString(locale, {
                day: "2-digit",
                month: "short",
                year: "numeric",
              });
            },
            label: (item) => {
              const value = formatNumber(Number(item.raw), locale, item.datasetIndex === 0 ? 1 : 0);
              if (item.datasetIndex === 0) return `${item.dataset.label}: ${value}`;
              return `${item.dataset.label}: ${value} ${labelsText.unit}`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            color: colors.text,
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: 12,
            font: { size: 11 },
          },
        },
        y: {
          beginAtZero: true,
          grid: { color: colors.grid },
          ticks: { color: colors.text, font: { size: 11 }, padding: 8 },
        },
      },
    }),
    [colors, locale, rawDates, labelsText],
  );

  // Plugin inline: divisor vertical "hoje" no último dia real.
  const todayDividerPlugin = useMemo<Plugin<"line">>(
    () => ({
      id: "todayDivider",
      afterDatasetsDraw: (chart) => {
        const idx = todayIndexRef.current;
        if (idx < 0) return;
        const { ctx, chartArea, scales } = chart;
        const x = scales.x.getPixelForValue(idx);
        if (!Number.isFinite(x)) return;
        ctx.save();
        ctx.beginPath();
        ctx.setLineDash([2, 3]);
        ctx.lineWidth = 1;
        ctx.strokeStyle = todayLineColorRef.current;
        ctx.moveTo(x, chartArea.top + 12);
        ctx.lineTo(x, chartArea.bottom);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = textColorRef.current;
        ctx.font = "600 10px Montserrat, sans-serif";
        ctx.textAlign = "center";
        ctx.fillText(todayLabelRef.current, x, chartArea.top + 4);
        ctx.restore();
      },
    }),
    [],
  );

  return (
    <div style={{ position: "relative", width: "100%", height }}>
      <Line data={data} options={options} plugins={[todayDividerPlugin]} />
    </div>
  );
};

export default ProductionChart;
